import { Table, THead, TBody, Tr, Th, Td } from "../ui/Table";
import { Button } from "../ui/Button";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";
import { formatRelative, formatExpiry } from "./relative-time";
import { useTranslation } from "../i18n";
import type { TokenRow } from "./types";

export interface TokensTableProps {
  tokens: TokenRow[];
  currentWorkspaceId: string | null;
  now?: Date;
  onRevoke: (token: TokenRow) => void;
}

export function TokensTable({ tokens, currentWorkspaceId, now, onRevoke }: TokensTableProps) {
  const { t } = useTranslation("tokens");
  const menu = useContextMenu();

  function menuItems(token: TokenRow): ContextMenuItem[] {
    return [
      { label: t("table.copyName"), onClick: () => void navigator.clipboard?.writeText(token.name) },
      { label: t("table.revoke"), onClick: () => onRevoke(token), danger: true, separator: true },
    ];
  }

  function workspaceLabel(workspaceId: string | null): string {
    if (workspaceId === null) return t("workspace.global");
    if (workspaceId === currentWorkspaceId) return t("workspace.this");
    return t("workspace.other");
  }

  if (tokens.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-700">
      {menu.node}
      <Table>
        <THead>
          <Tr>
            <Th>{t("table.name")}</Th>
            <Th>{t("table.scopes")}</Th>
            <Th>{t("table.workspace")}</Th>
            <Th>{t("table.lastUsed")}</Th>
            <Th>{t("table.expires")}</Th>
            <Th className="w-20" />
          </Tr>
        </THead>
        <TBody>
          {tokens.map((token) => {
            const expired =
              token.expiresAt !== null && new Date(token.expiresAt) < (now ?? new Date());
            return (
              <Tr key={token.id} role="row" onContextMenu={(e) => menu.open(e, menuItems(token))}>
                <Td>
                  <span className="font-medium text-slate-100">{token.name}</span>
                </Td>
                <Td className="text-slate-300">{token.scopes.join(", ")}</Td>
                <Td className="text-slate-400">
                  {workspaceLabel(token.workspaceId)}
                </Td>
                <Td className="text-slate-400">{formatRelative(token.lastUsedAt, now)}</Td>
                <Td
                  className="text-slate-400"
                  aria-label={expired ? t("table.expiredAria") : undefined}
                >
                  {formatExpiry(token.expiresAt, now)}
                </Td>
                <Td className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t("table.revokeAria", { name: token.name })}
                    onClick={() => onRevoke(token)}
                    className="text-red-400 hover:text-red-300"
                  >
                    {t("table.revoke")}
                  </Button>
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}
